import React, { useEffect, useRef, useContext, useState } from "react";
import { LandingPageContext } from "../../contexts/LandingPageContext";
import i18n from "../../i18n";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./Hero.css";

gsap.registerPlugin(ScrollTrigger);

const CLOUDINARY_BASE = 'https://res.cloudinary.com/dqssg8tws/image/upload';

const makeCloudinaryUrl = (originalUrl, opts = {}) => {
  // If original URL is already a Cloudinary URL, try to replace transformation chunk
  try {
    if (!originalUrl) return null;
    const publicIdMatch = originalUrl.match(/\/v\d+\/(.+)$/);
    const publicId = publicIdMatch ? publicIdMatch[1] : null;
    if (publicId) {
      const { w, q = 'auto', f = 'auto', extra } = opts;
      const segments = [];
      if (f) segments.push(`f_${f}`);
      if (q) segments.push(`q_${q}`);
      if (typeof w !== 'undefined' && w !== null) segments.push(`w_${w}`);
      if (extra) segments.push(extra);
      const transformation = segments.length ? `${segments.join(',')}/` : '';
      return `${CLOUDINARY_BASE}/${transformation}${publicId}`;
    }
  } catch (e) {
    // fallback
  }
  return null;
};

const Hero = () => {
    const { hero } = useContext(LandingPageContext);

    const heroRef = useRef(null);
    const bgRef = useRef(null);
    const titleRef = useRef(null);
    const subtitleRef = useRef(null);
    const [bgState, setBgState] = useState('loading');

    useEffect(() => {
        if (!heroRef.current || !bgRef.current || !titleRef.current || !subtitleRef.current) return;

        const ctx = gsap.context(() => {
            gsap.fromTo(
                bgRef.current,
                { scale: 1 },
                {
                    scale: 2,
                    ease: "none",
                    scrollTrigger: {
                        trigger: heroRef.current,
                        start: "top top",
                        end: "bottom top",
                        scrub: true
                    }
                }
            );

            gsap.fromTo(
                titleRef.current,
                { scale: 1 },
                {
                    scale: 4,
                    ease: "none",
                    scrollTrigger: {
                        trigger: heroRef.current,
                        start: "top top",
                        end: "bottom top",
                        scrub: true
                    }
                }
            );

            gsap.fromTo(
                subtitleRef.current,
                { scale: 1, y: 0 },
                {
                    scale: 1.3,
                    y: -40,
                    ease: "none",
                    scrollTrigger: {
                        trigger: heroRef.current,
                        start: "top top",
                        end: "bottom top",
                        scrub: true
                    }
                }
            );
        }, heroRef);

        return () => ctx.revert();
    }, []);

    // LQIP + full image swapping
    const heroImage = hero && hero.image ? hero.image : null;

    useEffect(() => {
      const bgEl = bgRef.current;
      if (!bgEl) return;

      let lqip = null;
      let highres = null;

      // If the image looks like a Cloudinary URL, build optimized versions
      if (heroImage && heroImage.includes('res.cloudinary.com')) {
        // try to build LQIP and highres
        const lqipUrl = makeCloudinaryUrl(heroImage, { w: 80, q: 10, f: 'auto', extra: 'e_blur:200' });
        // Do NOT change original sizing: omit width so Cloudinary returns the original dimensions (only format/quality optimized)
        const highUrl = makeCloudinaryUrl(heroImage, { q: 'auto', f: 'auto' });
        lqip = lqipUrl || heroImage;
        highres = highUrl || heroImage;
      } else if (heroImage) {
        // fallback: use original as highres
        lqip = heroImage;
        highres = heroImage;
      }

      // Apply LQIP first
      if (lqip) {
        bgEl.style.backgroundImage = `url(${lqip})`;
        bgEl.classList.remove('loaded');
        bgEl.classList.add('loading');
        setBgState('loading');
      }

      if (highres) {
        const img = new Image();
        img.src = highres;
        img.onload = () => {
          // swap to high-res
          try {
            bgEl.style.backgroundImage = `url(${highres})`;
            bgEl.classList.remove('loading');
            bgEl.classList.add('loaded');
            setBgState('loaded');
          } catch (e) {
            // ignore
          }
        };
        img.onerror = () => {
          // keep LQIP or fallback:
          bgEl.classList.remove('loading');
          bgEl.classList.add('loaded');
          setBgState('loaded');
        };
      }

      return () => {
        // no-op cleanup
      };
    }, [heroImage]);

    const resolveText = value =>
        typeof value === "object" && !Array.isArray(value)
            ? value[i18n.language] || value.de || value.en
            : value;

    return (
        <section
            id="home"
            className="hero"
            ref={heroRef}
            style={{
                backgroundColor: hero.backgroundColor,
                color: hero.textColor || "var(--global-hero-text-color, #ffffff)"
            }}
        >
            <div
                className={`hero-bg ${bgState}`}
                ref={bgRef}
                aria-hidden="true"
            />
            <div className="hero-content" style={{ color: hero.textColor || "var(--global-hero-text-color, #ffffff)" }}>
                <h1 className="hero-title" ref={titleRef}>
                    {resolveText(hero.title)}
                </h1>
                <h2 className="hero-subtitle" ref={subtitleRef}>
                    {resolveText(hero.subtitle)}
                </h2>
            </div>
        </section>
    );
};

export default Hero;
